/**
 * Email Service via Backend API
 * 
 * This service sends video link emails through a secure backend API.
 * The backend handles SMTP - NO credentials are exposed in frontend!
 */

// Configuration from environment variables
const config = {
    apiEndpoint: import.meta.env.VITE_EMAIL_API_ENDPOINT || '',
    recipientEmail: import.meta.env.VITE_VIDEO_RECIPIENT_EMAIL || 'shivam.sahane@addnectar.com',
    ccEmail: import.meta.env.VITE_VIDEO_CC_EMAIL || '',
};

/**
 * Generate viewer page URL from storage URL
 * Passes the full storage URL as a query parameter
 */
const generateViewerUrl = (storageUrl: string, productName?: string): string => {
    const baseUrl = window.location.origin;
    
    // Encode storage URL to handle special characters
    let viewerUrl = `${baseUrl}/viewer?url=${encodeURIComponent(storageUrl)}`;
    
    if (productName) {
        viewerUrl += `&product=${encodeURIComponent(productName)}`;
    }
    
    return viewerUrl;
};

/**
 * Send video link email via backend API
 * @param videoUrl - The public URL of the uploaded video (storage URL)
 * @param recipientEmail - Optional recipient email (defaults to env variable)
 * @param productName - Optional product name for email subject
 * @returns Promise resolving to success status
 */
export const sendVideoEmail = async (
    videoUrl: string, 
    recipientEmail?: string,
    productName?: string
): Promise<boolean> => {
    // Validate configuration
    if (!config.apiEndpoint) {
        console.error('[EmailService] Email API endpoint not configured. Please check your .env file.');
        throw new Error('Email service not configured. Please set VITE_EMAIL_API_ENDPOINT in .env');
    }

    // Generate branded viewer URL
    const viewerUrl = generateViewerUrl(videoUrl, productName);
    
    // console.log('[EmailService] Generated viewer URL:', viewerUrl);
    // console.log('[EmailService] Original storage URL:', videoUrl);

    // Determine recipients
    const rawRecipients = recipientEmail || config.recipientEmail;
    // Clean up recipients (handle comma+space)
    const to = rawRecipients ? rawRecipients.split(',').map(e => e.trim()).filter(e => e).join(',') : '';

    // Handle CC from config
    const rawCc = config.ccEmail;
    // Ensure CC is always an array of strings
    const cc = rawCc ? rawCc.split(',').map((e: string) => e.trim()).filter((e: string) => e) : [];

    const payload = {
        to: to,
        cc: cc, // Send as array ["email1", "email2"]
        videoUrl: viewerUrl, // Send viewer URL instead of storage URL
        subject: `360° Product Video Ready${productName ? ` - ${productName}` : ''} - AddUp Commerce`,
        message: `Your 360° product video is ready! Click the button in the email to view it.`,
    };

    try {
        // console.log('[EmailService] Sending email via backend API...');
        
        const response = await fetch(config.apiEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Email API error: ${response.status} - ${errorText}`);
        }

        await response.json();
        // console.log('[EmailService] Email sent successfully:', result);
        return true;
    } catch (error) {
        console.error('[EmailService] Failed to send email:', error);
        throw error;
    }
};

/**
 * Check if email service is properly configured
 */
export const isEmailConfigured = (): boolean => {
    return !!config.apiEndpoint;
};

/**
 * Get the configured recipient email
 */
export const getRecipientEmail = (): string => {
    return config.recipientEmail;
};
